import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MiniDaw } from '../features/midi-daw/components/MiniDaw';
import type { MidiExportData, MidiNote, DetectedChord, SplitLayoutConfig } from '../features/midi-daw/types/midi';
import { globalAudioEngine } from '../features/midi-daw/core/audioEngine';
import { RecordedMidiNote, TimeSignature } from '../types';
import { getAudioContext, muteEditorAudio, unmuteEditorAudio } from '../utils/audio';

const SONG_STYLES = ['Jazz', 'Swing', 'Latin', 'Ballad', 'Soul', 'Pop', 'Rock', 'Bossa', 'Country', 'Reggae'] as const;
const KUMBH = 'Kumbh Sans, sans-serif';
const PILL_BG = '#D9D9D9';
const FIELD_BG = '#F4F1EB';
const FIELD_TEXT = '#DB9817';
const TEXT_DARK = '#271B13';

interface MidiDawModalProps {
  isOpen: boolean;
  onClose: () => void;
  songTitle?: string;
  onSongTitleChange?: (title: string) => void;
  songAuthor?: string;
  onSongAuthorChange?: (author: string) => void;
  songStyle?: string;
  onSongStyleChange?: (style: string) => void;
  saveStatus?: 'idle' | 'saving' | 'saved';
  hasUnsavedChanges?: boolean;
  bpm: number;
  timeSignature: TimeSignature;
  recordedNotes: RecordedMidiNote[];
  onNotesChange: (notes: RecordedMidiNote[]) => void;
  midiInput: MIDIInput | null;
  // Chord grid bridge — lets the musician view the real Chord Notator grid (read-only)
  // from inside the Piano Recorder, same state, no second copy. Editing stays in Edit Mode.
  renderChartPane?: () => React.ReactNode;
  onSendChordsToChart?: (measureChords: Map<number, DetectedChord[]>) => void;
  // Jumps straight to the full-screen score (skips the piano roll) — used by Gig Mode's "Voir la partition".
  startInScoreFullScreen?: boolean;
}

/**
 * Host wrapper that bridges Chord Notator's chart state to the MiniDaw module
 * (features/midi-daw/). Keeps the integration contract isolated to this file.
 */
const MidiDawModal: React.FC<MidiDawModalProps> = ({
  isOpen,
  onClose,
  songTitle,
  onSongTitleChange,
  songAuthor,
  onSongAuthorChange,
  songStyle,
  onSongStyleChange,
  saveStatus = 'idle',
  hasUnsavedChanges = false,
  bpm,
  timeSignature,
  recordedNotes,
  onNotesChange,
  midiInput,
  renderChartPane,
  onSendChordsToChart,
  startInScoreFullScreen = false,
}) => {
  const [isFullScreenScore, setIsFullScreenScore] = useState(false);
  const [isTitlePanelOpen, setIsTitlePanelOpen] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState<SplitLayoutConfig>({
    paneCount: 1,
    direction: 'vertical',
    panes: ['pianoroll', 'score', 'chart'],
    splitRatios: [100],
  });

  useEffect(() => {
    if (isOpen && startInScoreFullScreen) {
      setIsFullScreenScore(true);
      // The full-screen overlay lives inside the Score pane's own component, so that pane must
      // actually be mounted (otherwise forcing isFullScreenScore has nothing to render over).
      setLayoutConfig((prev) => ({ ...prev, paneCount: 1, panes: ['score', 'pianoroll', 'chart'], splitRatios: [100] }));
    }
  }, [isOpen, startInScoreFullScreen]);

  // The Editor and the DAW share the same AudioContext — without this, a chord still ringing in
  // the Editor (or the DAW's transport/metronome) bleeds into the other when switching between them.
  // Skip the very first mount: the modal always renders (isOpen starts false) even for users who
  // never touch Piano Rec, so without this guard every page load eagerly spun up BOTH the
  // Editor's AudioContext (unmuteEditorAudio) AND the DAW's own internal one (stopAll -> getContext)
  // for no reason — two real AudioContexts created before the user ever opened the DAW.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isOpen) {
      muteEditorAudio();
    } else {
      unmuteEditorAudio();
      globalAudioEngine.stopAll();
    }
  }, [isOpen]);

  // MiniDaw only consumes `initialNotes` at mount; regenerate ids from the persisted contract shape.
  const initialNotes = useMemo<MidiNote[]>(
    () => recordedNotes.map((note, index) => ({ id: `rec-${index}-${note.startTick}-${note.pitch}`, ...note })),
    [recordedNotes]
  );

  const handleChange = (data: MidiExportData) => {
    onNotesChange(data.notes);
  };

  if (!isOpen) return null;

  const statusLabel = saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : hasUnsavedChanges ? 'Unsaved changes' : 'Up to date';
  const statusColor = saveStatus === 'saving' ? '#B45309' : saveStatus === 'saved' ? '#15803D' : hasUnsavedChanges ? '#B91C1C' : '#6B7280';

  return (
    <div className="fixed inset-0 z-[500] bg-[#F8F7F4] flex flex-col">
      <header className="relative flex items-center justify-between gap-3 px-4 h-12 shrink-0 bg-[#1B1B1B] text-white">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm font-semibold transition-colors shrink-0"
          aria-label="Close piano recorder"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Exit</span>
        </button>

        {/* Title/Author/Style pill — same pattern as the Edit Mode header dropdown */}
        <div className="flex-1 flex justify-center min-w-0">
          <button
            onClick={() => setIsTitlePanelOpen(v => !v)}
            aria-expanded={isTitlePanelOpen}
            aria-label="Edit song title"
            style={{
              backgroundColor: isTitlePanelOpen ? FIELD_BG : PILL_BG,
              borderRadius: isTitlePanelOpen ? '14px 14px 0 0' : 14,
              border: 'none',
              cursor: 'pointer',
              padding: '4px 16px',
              maxWidth: 280,
              fontFamily: KUMBH,
              fontSize: 13,
              color: TEXT_DARK,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {songTitle || 'Untitled Chart'}
          </button>
        </div>

        <span className="text-xs font-medium shrink-0" style={{ color: statusColor }}>
          {statusLabel}
        </span>

        {isTitlePanelOpen && (
          <>
            <div className="fixed inset-0 z-[600]" onClick={() => setIsTitlePanelOpen(false)} aria-hidden="true" />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                left: '50%',
                top: '100%',
                transform: 'translateX(-50%)',
                width: 280,
                zIndex: 601,
                backgroundColor: PILL_BG,
                borderRadius: '0 0 14px 14px',
                padding: '10px 14px 14px',
                boxShadow: '0px 6px 9.7px rgba(0,0,0,0.25)',
              }}
            >
              <div className="flex flex-col gap-2.5">
                <div>
                  <span style={{ fontFamily: KUMBH, fontSize: 11, color: TEXT_DARK }}>Title :</span>
                  <div style={{ borderRadius: 10, backgroundColor: FIELD_BG, padding: '4px 10px', marginTop: 2 }}>
                    <input
                      type="text"
                      value={songTitle ?? ''}
                      onChange={(e) => onSongTitleChange?.(e.target.value)}
                      autoFocus
                      placeholder="Song name"
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontSize: 13, color: FIELD_TEXT }}
                    />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: KUMBH, fontSize: 11, color: TEXT_DARK }}>Author :</span>
                  <div style={{ borderRadius: 10, backgroundColor: FIELD_BG, padding: '4px 10px', marginTop: 2 }}>
                    <input
                      type="text"
                      value={songAuthor ?? ''}
                      onChange={(e) => onSongAuthorChange?.(e.target.value)}
                      placeholder="Author name"
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontSize: 13, color: FIELD_TEXT }}
                    />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: KUMBH, fontSize: 11, color: TEXT_DARK }}>Style :</span>
                  <div style={{ borderRadius: 10, backgroundColor: FIELD_BG, padding: '4px 10px', marginTop: 2 }}>
                    <select
                      value={songStyle ?? 'Swing'}
                      onChange={(e) => onSongStyleChange?.(e.target.value)}
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontSize: 13, color: FIELD_TEXT, appearance: 'none' }}
                    >
                      {SONG_STYLES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </header>
      <div className="flex-1 min-h-0">
        <MiniDaw
          bpm={bpm}
          timeSignature={timeSignature}
          initialNotes={initialNotes}
          midiInput={midiInput}
          audioContext={getAudioContext()}
          onChange={handleChange}
          layoutConfig={layoutConfig}
          onLayoutConfigChange={setLayoutConfig}
          isFullScreenScoreOpen={isFullScreenScore}
          onToggleFullScreenScore={setIsFullScreenScore}
          renderChartPane={renderChartPane}
          onSendChordsToChart={onSendChordsToChart}
          className="w-full h-full"
        />
      </div>
    </div>
  );
};

export default MidiDawModal;
